/**
 * @flow
 *
 * A React wrapper around the native HTML <dialog> element.
 *
 * The native <dialog> element with showModal() provides:
 * - Focus trapping (built-in)
 * - aria-modal="true" (implicit)
 * - Escape key to close (built-in, fires 'cancel' event)
 * - Backdrop via ::backdrop pseudo-element
 * - Inert background (no need to manually set aria-hidden on app node)
 */

import { type Node, useEffect, useRef } from "react";
import { lockBodyScroll, unlockBodyScroll } from "../utils/bodyScrollLock";

// Flow doesn't include HTMLDialogElement in its built-in type definitions.
interface HTMLDialogElement extends HTMLElement {
  open: boolean;
  showModal(): void;
  close(returnValue?: string): void;
  returnValue: string;
}

type NativeDialogProps = {|
  children: Node,
  // Accessible name for the dialog. Provide titleText OR titleId, not both.
  titleText?: string,
  titleId?: string,
  // Called when the dialog is dismissed (escape key or backdrop click).
  onExit?: () => void,
  // Controls whether the dialog (and its children) are mounted. Defaults to true.
  // When false, the component returns null and children do not render, so child
  // effects don't fire while closed.
  mounted?: boolean,
  // CSS selector for the element to focus when the dialog opens.
  initialFocus?: string,
  // Additional class name(s) for the <dialog> element.
  dialogClassName?: string,
  // Inline styles for the <dialog> element.
  dialogStyle?: { [string]: string | number },
  // If true, clicking the backdrop closes the dialog.
  underlayClickExits?: boolean,
  // If false, the backdrop is transparent. Defaults to true.
  showBackdrop?: boolean,
|};

export const NativeDialog = ({
  children,
  titleText,
  titleId,
  onExit,
  mounted = true,
  initialFocus,
  dialogClassName,
  dialogStyle,
  underlayClickExits = false,
  showBackdrop = true,
}: NativeDialogProps): Node => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  // Open the dialog and lock body scroll on mount; cleanup restores both on unmount.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog == null) {
      return undefined;
    }

    // Capture the focused element so we can restore focus on close. Native <dialog>.close()
    // does this automatically, but we let React unmount the element instead of calling close(),
    // so we have to handle restoration ourselves.
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (!dialog.open) {
      dialog.showModal();
    }
    // Handle initial focus after the dialog is shown.
    if (initialFocus) {
      const focusTarget = dialog.querySelector(initialFocus);
      if (focusTarget instanceof HTMLElement) {
        focusTarget.focus();
      }
    }

    lockBodyScroll();
    return () => {
      unlockBodyScroll();
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [initialFocus, mounted]);

  // Set up event listeners for cancel (escape key) and backdrop click.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog == null) {
      return undefined;
    }

    const handleCancel = (event: Event) => {
      if (onExit) {
        event.preventDefault();
        onExit();
      }
      // Otherwise let the native default close fire — useful for ad-hoc consumers
      // that don't need state cleanup before close.
    };

    // Track mousedown so a drag that started on dialog content but ended on the backdrop
    // (e.g. selecting text and releasing outside) doesn't close the dialog.
    let mouseDownOnBackdrop = false;
    const handleMouseDown = (event: MouseEvent) => {
      mouseDownOnBackdrop = event.target === dialog;
    };
    const handleClick = (event: MouseEvent) => {
      if (underlayClickExits && mouseDownOnBackdrop && event.target === dialog && onExit) {
        onExit();
      }
      mouseDownOnBackdrop = false;
    };

    dialog.addEventListener("cancel", handleCancel);
    dialog.addEventListener("mousedown", handleMouseDown);
    dialog.addEventListener("click", handleClick);
    return () => {
      dialog.removeEventListener("cancel", handleCancel);
      dialog.removeEventListener("mousedown", handleMouseDown);
      dialog.removeEventListener("click", handleClick);
    };
  }, [onExit, underlayClickExits, mounted]);

  if (!mounted) {
    return null;
  }

  const classNames = ["native-dialog", showBackdrop ? "" : "native-dialog--no-backdrop", dialogClassName || ""]
    .filter(Boolean)
    .join(" ");

  const ariaLabel = titleText || undefined;
  const ariaLabelledBy = titleId || undefined;

  return (
    <dialog
      // $FlowFixMe[incompatible-type] - Flow's React DOM types don't include <dialog>, so the ref type doesn't match.
      ref={dialogRef}
      className={classNames}
      style={dialogStyle}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {children}
    </dialog>
  );
};
